import React, { Dispatch, SetStateAction, useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useForm } from 'react-hook-form';
import MyAppText from '../../../components/MyAppText';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import Toast from 'react-native-toast-message';
import { accessService } from '../../../services/central-api/access';
import { ClientError } from '../../../services/central-api/base-service';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    onConfirm: () => void;
    userId: number;
}

type BlockUserFormObject = {
    userBlockReason: string;
};

const defaultEmptyValues: BlockUserFormObject = {
    userBlockReason: '',
};

export default function BlockUserModal({ isModalVisible, setModalVisible, onConfirm, userId }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const form = useForm<BlockUserFormObject>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange',
    });

    async function formSubmit() {
        try {
            setIsLoading(true);
            const { userBlockReason } = form.getValues();
            await accessService.blockUser(userId, userBlockReason.trim());
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onConfirm();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message)
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
            closeModal();
        }
    }

    const closeModal = () => {
        form.reset(defaultEmptyValues);
        setModalVisible(false);
    };

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    <View style={{ flexDirection: 'column', rowGap: 8 }}>
                        <MyAppText style={styles.header}>{translate('blockUser')}</MyAppText>
                        <MyAppText style={styles.subheader}>{translate('provideBlockReason')}</MyAppText>
                    </View>
                    <FormInput
                        form={form}
                        fieldName='userBlockReason'
                        label={translate('blockReason')}
                        labelStyle={styles.reasonLabelStyle}
                        required
                    />
                    <FormActions
                        viewStyle={{ flex: 1, justifyContent: 'space-around', }}
                        buttonStyle={{ paddingHorizontal: 9, paddingVertical: 9 }}
                        buttonContentContainerStyle={{ flex: 1, }}
                        textStyle={{ fontSize: 16, fontWeight: '600' }}
                        cancelText='cancel'
                        confirmText='blockUser'
                        onSubmit={formSubmit}
                        onClose={closeModal}
                        disabled={isLoading || !form.formState.isValid}
                        isLoading={isLoading}
                    />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        rowGap: 32,
        padding: 32
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    highlightText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400'],
    },
    reasonLabelStyle: {
        color: theme.colors.neutral['500'],
        fontWeight: '600',
        fontSize: 14,
    }
}));
